// Structured Data for Rich Snippets and SEO

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "EMBER Impact Ventures LLC",
  "alternateName": ["EMBER", "EMBER Leadership"],
  "url": "https://emberimpact.com",
  "logo": {
    "@type": "ImageObject",
    "url": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
    "width": 200,
    "height": 200
  },
  "description": "EMBER Impact Ventures transforms leaders through authentic community, research-backed practices, and innovative programs including The Crucible peer accelerator, Spark Moments, and LIT Expeditions.",
  "foundingDate": "2024",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "hello@emberimpact.com",
    "availableLanguage": "English"
  },
  "sameAs": [
    "https://www.linkedin.com/company/ember-impact-ventures",
    "https://twitter.com/EmberLeadership"
  ],
  "services": [
    "Leadership Development",
    "Executive Coaching", 
    "Peer Learning Programs",
    "Leadership Training",
    "Professional Development"
  ]
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "EMBER Leadership Development",
  "alternateName": "EMBER Impact",
  "url": "https://emberimpact.com",
  "description": "Transform your leadership through authentic community and research-backed practices. Leadership isn't a title you earn—it's a flame you tend.",
  "publisher": {
    "@type": "Organization",
    "name": "EMBER Impact Ventures LLC",
    "logo": {
      "@type": "ImageObject", 
      "url": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
    }
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://emberimpact.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "Leaders, Executives, Professionals"
  }
};

export const courseSchema = (courseData) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": courseData.name,
  "description": courseData.description,
  "provider": {
    "@type": "Organization",
    "name": "EMBER Impact Ventures LLC",
    "url": "https://emberimpact.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
    }
  },
  "courseMode": courseData.mode || "online",
  "educationalLevel": "Professional Development",
  "subject": "Leadership Development",
  "timeRequired": courseData.duration,
  "startDate": courseData.startDate,
  "applicationDeadline": courseData.applicationDeadline,
  "maximumAttendeeCapacity": courseData.maxCapacity || 12,
  "offers": {
    "@type": "Offer",
    "price": courseData.price,
    "priceCurrency": "USD",
    "availability": "https://schema.org/LimitedAvailability",
    "validFrom": courseData.validFrom || "2025-01-01",
    "category": "Leadership Development"
  },
  "instructor": {
    "@type": "Person",
    "name": courseData.instructorName || "Matthew Callison, PhD",
    "description": courseData.instructorDescription || "Expert in creating psychological safety and facilitating breakthrough conversations among leaders"
  }
});

export const applicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "DailyGLOW",
  "description": "Daily leadership development app with research-backed reflection practices for sustainable growth.",
  "applicationCategory": "LifestyleApplication",
  "operatingSystem": ["iOS", "Android", "Web"],
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "1000",
    "bestRating": "5"
  },
  "author": {
    "@type": "Organization",
    "name": "EMBER Impact Ventures LLC"
  },
  "datePublished": "2024-01-01",
  "screenshot": "https://emberimpact.com/app-screenshot.png"
};

export const articleSchema = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.description,
  "author": {
    "@type": "Organization",
    "name": "EMBER Impact Ventures LLC",
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png"
    }
  },
  "publisher": {
    "@type": "Organization",
    "name": "EMBER",
    "logo": {
      "@type": "ImageObject",
      "url": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
      "width": 200,
      "height": 200
    }
  },
  "datePublished": article.datePublished || new Date().toISOString(),
  "dateModified": article.dateModified || new Date().toISOString(),
  "image": {
    "@type": "ImageObject",
    "url": article.image,
    "width": 1200,
    "height": 630
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": article.url
  },
  "wordCount": article.wordCount,
  "keywords": article.keywords
});

export const breadcrumbSchema = (breadcrumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://emberimpact.com${item.url}`
  }))
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer,
      "dateCreated": faq.dateCreated || new Date().toISOString()
    }
  }))
});

// Local Business Schema for service-based organization
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "EMBER Impact Ventures LLC",
  "image": "https://customer-assets.emergentagent.com/job_ember-ui-update/artifacts/y5fpxlpp_Ember-Logo-website.png",
  "url": "https://emberimpact.com",
  "telephone": "+1-XXX-XXX-XXXX", // Update with actual phone when available
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "40.7128", // Update with actual coordinates
    "longitude": "-74.0060"
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": [
      "Monday",
      "Tuesday", 
      "Wednesday",
      "Thursday",
      "Friday"
    ],
    "opens": "09:00",
    "closes": "17:00"
  },
  "priceRange": "$$",
  "serviceArea": {
    "@type": "Country",
    "name": "United States"
  }
};